import { IonButton, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonNote, IonPage, IonRadio, IonRadioGroup, IonText, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { addOutline, removeCircle } from 'ionicons/icons';
import { useIonModal } from '@ionic/react';
import { useState } from 'react';
import SelectRfidModal from '../SelectRfidModal';
import RfidChip from '../../components/RfidChip';
import styles from '../DoorPolicyModal.module.css';
import { modalController } from '@ionic/core';
import { useCommonTranslation, useModalTranslation } from '../../hooks/useTranslation';
import { useDeveloperModeQuery } from '../../query/developerModeQuery';

export interface EditDoorPolicyRfidPickerProps {
    deviceId: string;
    rfidCodes: string[];
    rfidTimeoutMs?: number;
    onSelectRfidCodes: (codes: string[]) => void;
    onSelectRfidTimeout: (rfidTimeoutMs?: number) => void;
    onClose: () => void;
}

const DEFAULT_TIMEOUT_SECONDS = 5;
const MIN_TIMEOUT_SECONDS = 0.1;
const MAX_TIMEOUT_SECONDS = 60;

// Timeouts are edited in seconds with 0.1s granularity (e.g. "1.5").
const clampTimeoutSeconds = (seconds: number) => {
    const rounded = Math.round(seconds * 10) / 10;
    return Math.min(Math.max(rounded, MIN_TIMEOUT_SECONDS), MAX_TIMEOUT_SECONDS);
};

const getInitialTimeoutSeconds = (rfidTimeoutMs?: number) => {
    if (rfidTimeoutMs && rfidTimeoutMs > 0) {
        return String(clampTimeoutSeconds(rfidTimeoutMs / 1000));
    }
    return String(DEFAULT_TIMEOUT_SECONDS);
};

const normalizeTimeoutValue = (value: string) => {
    const parsed = Number.parseFloat(value.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return String(DEFAULT_TIMEOUT_SECONDS);
    }
    return String(clampTimeoutSeconds(parsed));
};

const EditDoorPolicyRfidPicker = ({
    deviceId,
    rfidCodes,
    rfidTimeoutMs,
    onSelectRfidCodes,
    onSelectRfidTimeout,
    onClose,
}: EditDoorPolicyRfidPickerProps) => {
    const [selectedRfidCodes, setSelectedRfidCodes] = useState(rfidCodes);
    const [selectionMode, setSelectionMode] = useState<'any' | 'specific'>(
        rfidCodes.length ? 'specific' : 'any'
    );
    const [timeoutOption, setTimeoutOption] = useState<'forever' | 'timeout'>(
        rfidTimeoutMs !== undefined ? 'timeout' : 'forever'
    );
    const [timeoutValue, setTimeoutValue] = useState(() => getInitialTimeoutSeconds(rfidTimeoutMs));
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;
    const shouldShowTimeout = developerMode || rfidTimeoutMs !== undefined;
    const [presentSelectRfidModal, dismissSelectRfidModal] = useIonModal(SelectRfidModal, {
        dismiss: (rfidCode: string, role: string) => {
            if (role === 'confirm' && rfidCode) {
                const next = Array.from(new Set([...selectedRfidCodes, rfidCode]));
                setSelectedRfidCodes(next);
            }
            dismissSelectRfidModal(rfidCode, role);
        },
        deviceId,
        disabledRfidCodes: selectedRfidCodes,
    });

    const removeRfidCode = (rfidCode: string) => {
        const next = selectedRfidCodes.filter((code) => code !== rfidCode);
        setSelectedRfidCodes(next);
    };

    const addRfidCode = async () => {
        if (selectionMode !== 'specific') {
            return;
        }
        const presentingElement = await modalController.getTop();
        presentSelectRfidModal({
            presentingElement: presentingElement ?? undefined,
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={onClose}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    {/* ios title mode keeps the title centered in md too */}
                    <IonTitle {...({ mode: 'ios' } as object)}>{tModals('door_policy.pickers.rfid.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            strong={true}
                            onClick={() => {
                                if (selectionMode === 'any') {
                                    onSelectRfidCodes([]);
                                } else {
                                    onSelectRfidCodes(selectedRfidCodes);
                                }
                                if (timeoutOption === 'forever') {
                                    onSelectRfidTimeout(undefined);
                                } else {
                                    const normalizedValue = normalizeTimeoutValue(timeoutValue);
                                    onSelectRfidTimeout(Math.round(Number(normalizedValue) * 1000));
                                }
                                onClose();
                            }}
                        >
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <section className={`${styles.wrapper} ${styles.section}`}>
                    <h2 className={styles.sectionLabel}>{tModals('door_policy.pickers.rfid.heading')}</h2>
                    <IonList className={styles.listCard}>
                        <IonRadioGroup
                            value={selectionMode}
                            onIonChange={(event) => setSelectionMode(event.detail.value)}
                        >
                            <IonItem>
                                <IonRadio value="any" labelPlacement="end" justify="start">
                                    {tModals('door_policy.pickers.rfid.any_microchip')}
                                </IonRadio>
                            </IonItem>
                            <IonItem>
                                <IonRadio value="specific" labelPlacement="end" justify="start">
                                    {tModals('door_policy.pickers.rfid.specific_microchips')}
                                </IonRadio>
                            </IonItem>
                        </IonRadioGroup>
                        {selectionMode === 'specific' && (
                            <>
                                {selectedRfidCodes.map((rfidCode) => (
                                    <IonItem key={rfidCode} button detail={false} onClick={() => removeRfidCode(rfidCode)}>
                                        <IonIcon slot="start" icon={removeCircle} color="danger" />
                                        <RfidChip rfidCode={rfidCode} deviceId={deviceId} />
                                    </IonItem>
                                ))}
                                <IonItem button detail={false} onClick={addRfidCode}>
                                    <IonIcon slot="start" icon={addOutline} />
                                    <IonText color="primary">{tModals('door_policy.pickers.rfid.add_microchip')}</IonText>
                                </IonItem>
                            </>
                        )}
                    </IonList>
                    {shouldShowTimeout && (
                        <>
                            <h2 className={`${styles.sectionLabel} ${styles.sectionLabelSpaced}`}>
                                {tModals('door_policy.pickers.rfid.timeout_heading')}
                            </h2>
                            <IonList className={styles.listCard}>
                                <IonRadioGroup
                                    value={timeoutOption}
                                    onIonChange={(event) => {
                                        const nextOption = event.detail.value as 'forever' | 'timeout';
                                        setTimeoutOption(nextOption);
                                        if (nextOption === 'timeout') {
                                            setTimeoutValue(normalizeTimeoutValue(timeoutValue));
                                        }
                                    }}
                                >
                                    <IonItem>
                                        <IonRadio value="forever" labelPlacement="end" justify="start">
                                            <div className={styles.durationOption}>
                                                <div>{tModals('door_policy.pickers.rfid.wait_forever')}</div>
                                                <IonNote className={styles.durationNote}>
                                                    {tModals('door_policy.pickers.rfid.wait_forever_note')}
                                                </IonNote>
                                            </div>
                                        </IonRadio>
                                    </IonItem>
                                    <IonItem>
                                        <IonRadio value="timeout" labelPlacement="end" justify="start">
                                            <div className={styles.durationOption}>
                                                <div>{tModals('door_policy.pickers.rfid.give_up_after')}</div>
                                                <IonNote className={styles.durationNote}>
                                                    {tModals('door_policy.pickers.rfid.give_up_after_note')}
                                                </IonNote>
                                            </div>
                                        </IonRadio>
                                    </IonItem>
                                </IonRadioGroup>
                                {timeoutOption === 'timeout' && (
                                    <IonItem>
                                        <IonLabel position="stacked">
                                            {tModals('door_policy.pickers.rfid.duration_label')}
                                        </IonLabel>
                                        <IonInput
                                            type="number"
                                            inputmode="decimal"
                                            step="0.1"
                                            min={String(MIN_TIMEOUT_SECONDS)}
                                            max={String(MAX_TIMEOUT_SECONDS)}
                                            value={timeoutValue}
                                            onIonInput={(event) => setTimeoutValue(event.detail.value ?? '')}
                                            onIonBlur={() => setTimeoutValue(normalizeTimeoutValue(timeoutValue))}
                                        />
                                    </IonItem>
                                )}
                            </IonList>
                        </>
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export const EditDoorPolicyRfidPickerView = (props: EditDoorPolicyRfidPickerProps) => (
    <EditDoorPolicyRfidPicker {...props} />
);

export default EditDoorPolicyRfidPicker;
